import { createMemo, createSignal, createEffect, createResource, For, Show, Suspense, JSXElement } from "solid-js";

import { getFolderContents, createS3Folder } from "~/_server/s3";
import { UploadManager } from "~/components/FullScreenUpload";
import { useRIASApp } from "~/_context/UserProvider";

const getFileName = ( key: string ): string =>
	key.split( "/" ).filter( Boolean ).pop() ?? "";

const getFileType = ( key: string ): string | null => {
	const extension = key.split( "." ).filter( Boolean ).pop()?.toLowerCase() ?? "";
	const knownTypes = ["pdf", "doc", "docx", "xls", "xlsx"];
	return knownTypes.includes( extension ) ? extension : null;
};
const formatFileSize = ( bytes: number, decimals = 1 ) => {
	if ( bytes === 0 ) return "0 B";
	const k = 1024;
	const sizes = ["B", "KB", "MB", "GB", "TB", "PB"];
	const i = Math.floor( Math.log( bytes ) / Math.log( k ) );
	const dm = decimals < 0 || i < 2 ? 0 : decimals;
	return parseFloat( ( bytes / Math.pow( k, i ) ).toFixed( dm ) ) + " " + sizes[i];
};

export const LogBook = ( props: { userId: string, skipShared: boolean } ) => {
	let uploadMgr: JSXElement;

	const [sidebarOpen, setSidebarOpen] = createSignal( false );

	let sections = createMemo( () => {
		const baseSections = [
			{ name: "1 - Scheme Documents", prefix: "logbook/shared/scheme-documents/" },
			{ name: "2 - Quality Assurance", prefix: `logbook/${props.userId}/quality-assurance/` },
			{ name: "3A - Regulatory Documents", prefix: `logbook/shared/regulatory-documents/` },
			{ name: "3B - Technical Standards and Guidance", prefix: `logbook/shared/technical-standards/` },
			{ name: "3C - SAP Methodology and Conventions", prefix: `logbook/shared/sap/` },
			{ name: "3D - Additional Standards & Documents", prefix: `logbook/${props.userId}/additional-standards/` },
			{ name: "4 - PI Insurance Certificates", prefix: `logbook/${props.userId}/pi/` },
			{ name: "5 - CPD Logbooks", prefix: `logbook/${props.userId}/logbooks/` },
			{ name: "6 - Correspondance with Scheme", prefix: `logbook/${props.userId}/correspondance/` },
			{ name: "7 - Previous Audit Reports", prefix: `logbook/${props.userId}/audit-reports/` },
			{ name: "8 - Certificates Issued", prefix: `logbook/${props.userId}/certificates-issued/` },
			{ name: "9 - Scheme Code of Conduct - Signed and Scanned", prefix: `logbook/${props.userId}/code-of-conduct/` },
			{ name: "10 - Membership Certificates", prefix: `logbook/${props.userId}/membership-certificates/` },
			{ name: "11 - SBEM", prefix: `logbook/${props.userId}/sbem/` },
		];

		return props.skipShared ? baseSections.filter( section => !section.prefix.includes( "shared" ) ) : baseSections;
	} );

	const [prefix, setPrefix] = createSignal( sections()[0].prefix );

	const currentSection = createMemo( () => sections().find( section => prefix().startsWith( section.prefix ) ) );

	const [objects, { refetch: refetchS3Path }] = createResource( prefix, ( path: string ) => getFolderContents( path ), );

	const goUp = () => {
		const parts = prefix().split( "/" ).filter( Boolean );
		parts.pop();
		setPrefix( parts.length ? parts.join( "/" ) + "/" : "" );
	};

	const addFolder = async ( prefix: string, folder: string ) => {
		await createS3Folder( `${prefix}${folder}/` );

		refetchS3Path();
	}

	return (
		<>
			{/* mobile top bar */}
			<div class="lg:hidden flex items-center justify-between p-4 border-b">
				<h2 class="text-lg font-semibold">Log Book</h2>
				<button
					type="button"
					class="p-2 border rounded"
					onClick={() => setSidebarOpen( !sidebarOpen() )}
				>
					☰
				</button>
			</div>

			<div class="relative flex flex-col lg:flex-row text-gray-800 font-sans">
				{/* Sidebar */}
				<aside
					class={`fixed inset-y-0 left-0 z-40 bg-white lg:bg-transparent shadow-lg transform transition-transform
				  ${sidebarOpen() ? "translate-x-0" : "-translate-x-full"}
				  lg:static lg:translate-x-0 lg:w-fit p-6 space-y-6`}
				>
					<h2 class="text-xl font-semibold">Sections</h2>
					<nav class="space-y-2 text-sm">
						<For each={sections()}>
							{( section ) => (
								<p
									class="p-2 cursor-pointer hover:bg-accent hover:text-white truncate"
									classList={{
										"bg-accent": prefix()?.startsWith( section.prefix ),
										"font-bold": prefix()?.startsWith( section.prefix ),
									}}
									onClick={() => {
										setPrefix( section.prefix );
										setSidebarOpen( false ); // close on mobile
									}}
								>
									{section.name}
								</p>
							)}
						</For>
					</nav>

					<Show when={!props.skipShared}>
						<h3 class="font-bold m-0">About Your Logbook</h3>

						<p class="w-72 text-sm">Your logbook is both a resource of scheme information <em>and</em> the place to store documents that support your work as a Certifier of Desgin.</p>
					</Show>
				</aside>

				{/* Main content */}
				<main class="flex-1 p-4 lg:p-12 overflow-y-auto">

					<h2 class="mb-4 text-2xl font-semibold">{currentSection()?.name} / {prefix().replace( currentSection()?.prefix, "" ).replace( /\/$/, " / " )}</h2>
					{/* Folders */}
					<section>
						<h3 class="text-lg font-semibold mb-4">Folders</h3>
						<div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
							<Suspense fallback={<div>Loading…</div>}>
								<Show when={prefix().split( "/" ).length > 4}>
									<div
										class="flex flex-col items-center p-4 cursor-pointer text-primary"
										onClick={goUp}
									>
										<img src="/images/icon-back.svg" class="w-10" />
										<span>Back</span>
									</div>
								</Show>

								<Show when={objects.latest}>
									<For each={objects.latest?.folders}>
										{( folder ) => (
											<div
												class="flex flex-col items-center cursor-pointer text-primary p-4 border border-gray-300 rounded hover:bg-gray-50"
												onClick={() => setPrefix( folder )}
											>
												<img
													src="/images/icon-folder.svg"
													class="h-8 w-8 mb-1"
												/>
												<span class="text-xs truncate w-full text-center">
													{getFileName( folder as string )}
												</span>
											</div>
										)}
									</For>
									<Show when={prefix().includes( "shared" ) && objects.latest?.folders.length == 0}>
										No folders found.
									</Show>
								</Show>

								<Show when={!prefix().includes( "shared" )}>
									<EmptyFolder prefix={prefix()} addFolder={addFolder} />
								</Show>
							</Suspense>
						</div>
					</section>

					{/* Files */}
					<section class="mt-6">
						<h3 class="text-lg font-semibold mb-4">Files</h3>
						<div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
							<Suspense fallback={<div>Loading…</div>}>
								<Show when={objects.latest}>
									<For each={objects.latest?.files}>
										{( file ) => (
											<a href={`/api/download?key=${encodeURIComponent( file.key as string )}`} target="_blank" class="flex flex-col items-center justify-center p-4 border rounded border-gray-300 hover:bg-gray-50 cursor-pointer">
												<img
													src={`/images/icon-${getFileType( file.key as string )?.substring( 0, 3 ) ?? "unknown"}.svg`}
													class="h-8 w-8 mb-1"
													alt={getFileType( file.key as string ) ?? "Unknown File Type"}
												/>
												<span class="text-xs truncate w-full text-center">
													{getFileName( file.key as string )}
												</span>
												<span class="text-[10px] text-gray-500">
													{formatFileSize( file.size )}
												</span>
											</a>
										)}
									</For>

									<Show when={prefix().includes( "shared" ) && !objects.latest?.files}>
										No files found.
									</Show>
								</Show>
							</Suspense>

							<Show when={!prefix().includes( "shared" )}>
								<div
									onClick={() => uploadMgr?.triggerPicker()}
									class="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded cursor-pointer hover:border-accent">
									<img src="/images/icon-plus.svg" class="w-8" alt="Upload Files" /><span>Upload Files</span>
								</div>
							</Show>
						</div>
					</section>

					<Show when={prefix().includes( "shared" ) && !props.skipShared}>
						<img src="/images/icon-info.svg" class="h-8 w-8 absolute bottom-4 right-4" title="Note that you are not able to add files to those folders that are shared by all certifiers." />
					</Show>

					<Show when={!prefix().includes( "shared" )}>
						<UploadManager ref={( el ) => uploadMgr = el} path={prefix()} onComplete={() => refetchS3Path()} />
					</Show>
				</main>
			</div>
		</>
	);
};




const EmptyFolder = ( props ) => {
	let inputRef;

	const [create, setCreate] = createSignal( false );

	createEffect( () => {
		if ( create() && inputRef ) {
			inputRef.focus();
		}
	} );

	return (
		<div onClick={() => setCreate( true )} class="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded cursor-pointer hover:border-accent">
			<img src="/images/icon-plus.svg" class="w-8" alt="Add Folder" />
			<Show when={!create()}>
				<span>Add Folder</span>
			</Show>

			<Show when={create()}><input ref={( el ) => inputRef = el} onChange={async e => { await props.addFolder( props.prefix, e.target.value ); setCreate( false ) }} type="text" placeholder="Enter a name for the folder name - leave empty to cancel" /></Show>
		</div>

	);
};